import React, { useState } from 'react';
import { TrendingUp, RefreshCw, Sparkles, CheckCircle } from 'lucide-react';
import { MarketRate } from './MarketRate';
import { RateDifference } from './RateDifference';
import { useHybridRates } from '../../../hooks/useHybridRates';
import { LoadingSpinner } from '../../shared/LoadingSpinner';
import { ErrorAlert } from '../../shared/ErrorAlert';

interface RateComparisonProps {
  fromCurrency: string;
  toCurrency: string;
}

export function RateComparison({
  fromCurrency,
  toCurrency,
}: RateComparisonProps) {
  const [useAI, setUseAI] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const {
    officialRates,
    parallelRates,
    isLoading,
    isError,
    refetch,
    source,
    aiValidated,
    forceRefreshWithAI
  } = useHybridRates(useAI);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      if (useAI) {
        await forceRefreshWithAI();
      } else {
        await refetch();
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <TrendingUp className="h-5 w-5 text-emerald-600" />
          <h2 className="text-xl font-semibold">Market Comparison</h2>
          <RefreshCw className="h-4 w-4 text-gray-400 animate-spin" />
        </div>
        <LoadingSpinner />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <TrendingUp className="h-5 w-5 text-emerald-600" />
          <h2 className="text-xl font-semibold">Market Comparison</h2>
          <button
            onClick={handleRefresh}
            className="text-emerald-600 hover:text-emerald-700"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
        <ErrorAlert message="Failed to load rate comparison data" />
      </div>
    );
  }

  const parallelRate = officialRates?.find(rate => rate.currency === fromCurrency)?.centralRate || 0;
  const officialRate = parallelRates?.find(rate => rate.fromCurrency === fromCurrency)?.rate || 0;

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-emerald-600" />
          <h2 className="text-xl font-semibold">Live Market Comparison</h2>
          {aiValidated && (
            <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
              <CheckCircle className="h-3 w-3" />
              AI Verified
            </div>
          )}
          {source === 'ai' && (
            <div className="flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
              <Sparkles className="h-3 w-3" />
              AI Enhanced
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={useAI}
              onChange={(e) => setUseAI(e.target.checked)}
              className="rounded text-emerald-600 focus:ring-emerald-500"
            />
            AI Mode
          </label>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700
                     border border-emerald-200 rounded-lg px-3 py-1.5 hover:bg-emerald-50 transition-colors
                     disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <MarketRate
          type="official"
          rate={officialRate}
          currency={toCurrency}
          lastUpdated={parallelRates?.find(rate => rate.fromCurrency === fromCurrency)?.timestamp}
        />
        <MarketRate
          type="parallel"
          rate={parallelRate}
          currency={toCurrency}
          lastUpdated={officialRates?.find(rate => rate.currency === fromCurrency)?.lastUpdated}
        />
      </div>

      <RateDifference 
        officialRate={officialRate} 
        parallelRate={parallelRate} 
      />

      <div className="mt-4 text-center">
        <div className="text-xs text-gray-500 mb-2">
          CBN Official: {officialRate.toFixed(2)} ₦ • Parallel Market rates updated live
        </div>
        <div className="flex items-center justify-center gap-4 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
            <span className="text-blue-600">Official: Live</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
            <span className="text-purple-600">Parallel Market: Live</span>
          </div>
        </div>
        <div className="mt-2 text-xs text-gray-400">
          {source === 'ai' && 'Rates powered by OpenAI & Google Gemini AI'}
          {source === 'hybrid' && 'Rates from traditional APIs + AI validation'}
          {source === 'api' && 'Rates from interbank forex data providers'}
          {source === 'fallback' && 'Using cached fallback rates'}
        </div>
      </div>
    </div>
  );
}